import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { usePriorities, useProjectStatuses, useTaskStatuses } from '../../api/queries'
import { Card, ErrorBox, Loading } from '../../components/ui'
import type { Lookup } from '../../api/types'

/**
 * Statusser og prioriteter (kravspec afsnit 5, 15 og 13).
 * Farverne her styrer også TV-dashboardet, så ændringer slår igennem med det samme.
 */
export function StatusesPage() {
  const projectStatuses = useProjectStatuses()
  const taskStatuses = useTaskStatuses()
  const priorities = usePriorities()

  if (projectStatuses.isLoading || taskStatuses.isLoading || priorities.isLoading) return <Loading />
  if (projectStatuses.error) return <ErrorBox error={projectStatuses.error} />

  return (
    <div className="stack" style={{ gap: 16 }}>
      <LookupTable
        title="Projektstatusser"
        items={projectStatuses.data ?? []}
        endpoint="/api/project-statuses"
        queryKey={['lookups', 'project-statuses']}
      />
      <LookupTable
        title="Opgavestatusser"
        items={taskStatuses.data ?? []}
        endpoint="/api/task-statuses"
        queryKey={['lookups', 'task-statuses']}
      />
      <LookupTable
        title="Prioriteter"
        items={priorities.data ?? []}
        endpoint="/api/priorities"
        queryKey={['lookups', 'priorities']}
        showWeight
      />
    </div>
  )
}

function LookupTable({
  title,
  items,
  endpoint,
  queryKey,
  showWeight = false
}: {
  title: string
  items: Lookup[]
  endpoint: string
  queryKey: string[]
  showWeight?: boolean
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  async function save(item: Lookup, changes: Partial<Lookup>) {
    setError(null)
    const next = { ...item, ...changes }
    try {
      await api.put(`${endpoint}/${item.id}`, {
        name: next.name,
        color: next.color,
        icon: next.icon,
        isActive: next.isActive,
        isCompleted: next.isCompleted,
        isWaiting: next.isWaiting,
        sortOrder: next.sortOrder,
        weight: next.weight
      })
      await queryClient.invalidateQueries({ queryKey })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme.')
    }
  }

  return (
    <Card title={title}>
      {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Navn</th>
              <th>Farve</th>
              {showWeight && <th>Vægt</th>}
              <th>Sortering</th>
              <th>Systemkode</th>
              <th>Aktiv</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>
                  <input
                    defaultValue={item.name}
                    onBlur={(event) => {
                      if (event.target.value !== item.name) void save(item, { name: event.target.value })
                    }}
                  />
                </td>
                <td>
                  <div className="row">
                    <input
                      type="color"
                      style={{ width: 46, padding: 2 }}
                      defaultValue={item.color}
                      onChange={(event) => void save(item, { color: event.target.value })}
                    />
                    <span className="tiny muted">{item.color}</span>
                  </div>
                </td>
                {showWeight && (
                  <td style={{ width: 90 }}>
                    <input
                      type="number"
                      defaultValue={item.weight}
                      onBlur={(event) => void save(item, { weight: Number(event.target.value) })}
                    />
                  </td>
                )}
                <td style={{ width: 90 }}>
                  <input
                    type="number"
                    defaultValue={item.sortOrder}
                    onBlur={(event) => void save(item, { sortOrder: Number(event.target.value) })}
                  />
                </td>
                <td className="tiny muted">{item.systemCode ?? '-'}</td>
                <td>
                  <button
                    className={`btn btn--sm ${item.isActive ? 'btn--primary' : ''}`}
                    onClick={() => void save(item, { isActive: !item.isActive })}
                  >
                    {item.isActive ? 'Aktiv' : 'Skjult'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tiny muted" style={{ marginBottom: 0 }}>
        Rækker med systemkode bruges af forretningslogikken (f.eks. fremdriftsberegning) og kan ikke slettes.
      </p>
    </Card>
  )
}
